import { useApolloClient } from "@apollo/client";
import { addMinutes, differenceInCalendarDays, differenceInCalendarMonths } from "date-fns";
import debounce from "lodash/debounce";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { AppContext, BookingData, ServiceDuration, TimeSlot } from "../../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, CalendarDatePicker, Divider, Input, InputElement, LoadingSpinner, NavButtons, Notification } from "../../../common/components";
import { Label } from "../../../common/components/Input/common";
import { marginBottom, marginTop } from "../../../common/styles";
import { getCurrencyDisplay, getDurationDisplay, getFirstStep, requireBookingDataFields, today } from "../../../utils";
import { PartRequired } from "../../../utils/types";
import { NoAvailability } from "../NoAvailability/NoAvailability";
import { TimezoneSelector } from "../TimezoneSelector/TimezoneSelector";
import { TimeSlotSelection } from "./TimeSlotSelection/TimeSlotSelection";
type CompleteStepOpts = {
  attendees: number;
  duration: ServiceDuration;
  selectedTimeSlot: TimeSlot;
};
type CalendarViewProps = {
  attendees: number;
  availableDates: Date[];
  createReservationLoading: boolean;
  duration: ServiceDuration;
  hasSearchedFurtherAhead: boolean;
  loading?: boolean;
  monthOffset: number;
  onAttendeesChange: (quantity: number) => void;
  onCompleteStep: (options: CompleteStepOpts) => void;
  onDurationChange: (duration: ServiceDuration) => void;
  onGetMoreAvailableDates: () => void;
  onGoBack: () => void;
  onMonthChange: (monthOffset: number) => void;
};
type AvailabilityStepBookingData = PartRequired<BookingData, "organisation" | "service">;
export function CalendarView({
  attendees,
  availableDates,
  createReservationLoading,
  duration,
  hasSearchedFurtherAhead,
  loading,
  monthOffset,
  onAttendeesChange,
  onCompleteStep,
  onDurationChange,
  onGetMoreAvailableDates,
  onGoBack,
  onMonthChange
}: CalendarViewProps): React.ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const theme = useTheme();
  const client = useApolloClient();

  /**
   * The date the user has selected on the date picker.
   * The selected date is used in the availability intervals (time slots) request.
   */
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [timeSlotsLoaded, setTimeSlotsLoaded] = useState(false);
  const [timeSlotsExist, setTimeSlotsExist] = useState(false);

  /**
   * This combines the loading state of the the availabilityDates
   * query and the availabilityIntervals query.
   *
   * We track them both to avoid having flickering
   * multiple loading spinners. This means the loading spinner
   * will stop spinning when both queries are finished,
   */
  const combinedLoading = loading || !timeSlotsLoaded;
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | null>(null);
  const {
    appData
  } = useContext(AppContext);
  requireBookingDataFields<AvailabilityStepBookingData>(appData.bookingData, ["organisation", "service"]);
  const {
    service,
    organisation,
    pricePoints
  } = appData.bookingData;
  const {
    durations
  } = service;
  const isFirstStep = getFirstStep({
    bookingData: appData.bookingData,
    bookingTool: appData.bookingTool
  }) === "availability";

  /**
   * This is falling back to 500 because the value is currently incorrectly
   * marked as optional on the back-end but is a required field.
   *
   * It defaults to 500 in the rest of the Appointedd booking tools so
   * falling back to 500 for now until time is taken to make this a mandatory field.
   */
  const maxParticipantsPerBooking = service.maxParticipantsPerBooking ?? 500;
  const timeSlotSelected = (selectedTimeSlot: TimeSlot | undefined) => {
    setValidationError(null);
    setSelectedTimeSlot(selectedTimeSlot);
  };

  /**
   * This callback function is called when the user
   * selects an available date from the date picker.
   */
  const dateSelected = useCallback((newSelectedDate: Date) => {
    if (newSelectedDate !== selectedDate) {
      void client.clearStore().then(() => {
        setSelectedDate(newSelectedDate);
        setTimeSlotsLoaded(false);

        /**
         * Clear the selected slot when we change day.
         * This is so the ui doesn't show a selected slot
         * for the previously selected day
         */
        if (selectedTimeSlot) {
          timeSlotSelected(undefined);
        }
      });
    }
  }, [client, selectedDate, selectedTimeSlot]);
  const completeStep = () => {
    if (!selectedTimeSlot) {
      setValidationError(t("errors.noSelectedInterval"));
      return;
    }
    onCompleteStep({
      attendees,
      duration,
      selectedTimeSlot
    });
  };
  const durationOptions = durations.map((duration, index) => {
    let valueString = getDurationDisplay(duration, t);
    const currency = organisation.currency;
    const price = duration.price ?? 0;
    const priceDisplay = price === 0 ? undefined : getCurrencyDisplay(price, currency, i18n.language);
    if (priceDisplay) {
      valueString += ` - ${priceDisplay}`;
    }
    const {
      oldPrice
    } = duration;
    if (typeof oldPrice === "number" && oldPrice > price) {
      const oldPriceDisplay = getCurrencyDisplay(oldPrice, currency, i18n.language);
      valueString += ` (${t("wasValue", {
        value: oldPriceDisplay
      })})`;
    }
    return {
      optionId: `${index}`,
      value: valueString
    };
  });
  const showAttendeesInput = maxParticipantsPerBooking && maxParticipantsPerBooking > 1 && !pricePoints;
  const showDurationDropdown = durations.length > 1;
  const getSelectedOption = () => {
    const selectedOptionIndex = durations.findIndex(d => {
      return d.default === duration.default && d.minutes === duration.minutes && d.price === duration.price;
    });
    return `${selectedOptionIndex}`;
  };
  const changeAttendees = (quantity?: number) => {
    if (quantity) {
      setTimeSlotsLoaded(false);
      timeSlotSelected(undefined);
      onAttendeesChange(quantity);
    }
  };
  const changeDuration = (event: React.ChangeEvent<InputElement>) => {
    const selectedIndex = event.target.selectedIndex;
    const selectedDuration = durations.find((_d, index) => {
      return index === selectedIndex;
    });
    if (selectedDuration) {
      setTimeSlotsLoaded(false);
      timeSlotSelected(undefined);
      onDurationChange(selectedDuration);
    }
  };

  /**
   * This callback function is called any time the user
   * navigates through the months either via the prev and next arrow
   * buttons or by using the keyboard.
   */
  const changeMonth = (newOffset: number) => {
    setTimeSlotsLoaded(false);
    timeSlotSelected(undefined);
    onMonthChange(newOffset);
  };
  const onTimeSlotsLoaded = (hasTimeSlots: boolean) => {
    setTimeSlotsExist(hasTimeSlots);
    setTimeSlotsLoaded(true);
  };
  useEffect(() => {
    /**
     * Auto select the first available date from
     * the array of availableDates.
     */

    const firstAvailableDate = availableDates ? availableDates[0] : undefined;
    if (loading || !firstAvailableDate) return;

    /**
     * We need to check if the first available date is after the selected date.
     * If it is, we need to select the first available date.
     */
    const firstAvailableDateDifferenceInDays = firstAvailableDate && selectedDate ? differenceInCalendarDays(firstAvailableDate, selectedDate) : 0;
    if (!selectedDate || firstAvailableDateDifferenceInDays > 0) {
      // Offset the first available date with the local time zone otherwise the
      // time slots functionality will query the wrong date for intervals.
      const firstAvailableDateWithTZOffset = addMinutes(firstAvailableDate, firstAvailableDate.getTimezoneOffset());
      dateSelected(firstAvailableDateWithTZOffset);
      const monthOffsetTodayDifference = differenceInCalendarMonths(firstAvailableDateWithTZOffset, today);
      if (monthOffsetTodayDifference !== monthOffset) {
        onMonthChange(monthOffsetTodayDifference);
      }
    }
  }, [availableDates, dateSelected, loading, monthOffset, onMonthChange, selectedDate]);
  const updateCalendarAvailability = useMemo(
  /**
   * debounce to give user time to put correct number of
   *  attendees before refreshinh availability
   * */
  () => debounce((numberOfAttendees: number | undefined) => changeAttendees(numberOfAttendees), 1000),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  useEffect(() => {
    return () => updateCalendarAvailability.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Container data-sentry-element="Container" data-sentry-component="CalendarView" data-sentry-source-file="CalendarView.tsx">
      {showAttendeesInput && <AttendeesContainer>
          <AttendeesLabel>{t("attendee_other")}</AttendeesLabel>
          <Input variant="number" value={attendees} disabled={loading} id="attendeesNumberInput" aria-label={t("selectQuantity")} max={maxParticipantsPerBooking} min={1} onValueChange={quantity => updateCalendarAvailability(quantity)} />
        </AttendeesContainer>}

      {showDurationDropdown && <Input variant="dropdown" id={"durationDropdownInput"} aria-label={t("durationDropdownInput")} onChange={changeDuration} label={t("duration_one")} options={durationOptions} disabled={loading} hidePlaceholder selectedOption={getSelectedOption()} />}

      {(showAttendeesInput || showDurationDropdown) && <HorizontalDivider />}

      <TimezoneSelector disabled={loading} data-sentry-element="TimezoneSelector" data-sentry-source-file="CalendarView.tsx" />

      <CalendarContainer data-sentry-element="CalendarContainer" data-sentry-source-file="CalendarView.tsx">
        {combinedLoading && <LoadingPill />}

        {!loading && (!timeSlotsExist && timeSlotsLoaded || availableDates && availableDates.length === 0 && monthOffset >= 0) && <NoAvailability hasSearchedFurtherAhead={hasSearchedFurtherAhead} monthOffset={monthOffset} onSearchMore={onGetMoreAvailableDates} />}
        <CalendarDatePicker selectedDate={selectedDate} availableDates={availableDates} monthOffset={monthOffset} onSelectDate={dateSelected} onMonthChange={changeMonth} data-sentry-element="CalendarDatePicker" data-sentry-source-file="CalendarView.tsx" />
        {selectedDate && availableDates.length > 0 ? <TimeSlotSelection attendees={attendees} duration={duration} selectedDate={selectedDate} selectedTimeSlot={selectedTimeSlot} onSelectTimeSlot={timeSlotSelected} onTimeSlotsLoaded={onTimeSlotsLoaded} /> : <NoDatesOrTimeSlotsContainer />}
      </CalendarContainer>

      {validationError && <ErrorNotification variant="warning">
          {validationError}
        </ErrorNotification>}
      <NavButtons {...{
      hideBack: isFirstStep,
      onBack: onGoBack,
      onContinue: completeStep,
      ...((createReservationLoading || appData.reservationOnly) && {
        continueLabel: createReservationLoading ? <LoadingSpinner colour={theme.colours.textOnPrimary} /> : t("reserve")
      })
    }} data-sentry-element="NavButtons" data-sentry-source-file="CalendarView.tsx" />
    </Container>;
}
const Container = styled.div`
  display: flex;
  flex-direction: column;
`;
const NoDatesOrTimeSlotsContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;
const AttendeesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${marginBottom.m}
`;
const AttendeesLabel = styled(Label)`
  text-transform: capitalize;
`;
const HorizontalDivider = styled(Divider).attrs({
  vertical: false
})`
  display: flex;
  ${marginBottom.m}
  ${marginTop.m}
`;
const CalendarContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
  column-gap: 2.25rem;
  row-gap: 1rem;
`;
const LoadingPill = styled(BaseLoadingPill)`
  position: absolute;
  left: 45%;
  top: 45%;
`;
const ErrorNotification = styled(Notification)`
  ${marginTop.m}
`;